const API_URL = process.env.REACT_APP_API_URL;

export const openBankingService = {
  /**
   * Bank list for the picker. Already filtered server-side to exclude
   * banks Stitch can't verify (e.g. Bank Zero).
   */
  async getBanks() {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/api/open-banking/banks`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message);
      return result.banks;
    } catch (error) {
      console.error("Error getting bank list:", error);
      throw error;
    }
  },

  /**
   * Submits the account details form for verification.
   *
   * payload shape:
   *   { accountHolderType: 'individual', accountNumber, bankId, accountType,
   *     initials, familyName, idNumber, idCountry, phone, email }
   * or:
   *   { accountHolderType: 'business', accountNumber, bankId, accountType,
   *     businessName, registrationNumber, registrationCountry }
   *
   * Returns { status: 'verified' | 'failed' | 'review' | 'pending', details }.
   * On 'pending', the caller should wait a few seconds and call this again
   * with the same payload — Stitch caches results for 48h, so resubmitting
   * isn't billed again.
   */
  async verifyAccount(payload) {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/api/open-banking/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message);
      return result;
    } catch (error) {
      console.error("Error verifying bank account:", error);
      throw error;
    }
  },

  /**
   * Saves account details WITHOUT running Stitch verification — used
   * while Stitch credentials aren't available yet. Stores the same
   * fields as verifyAccount but marks the record as "unverified" instead
   * of running the BAV check. Swap the form's submit handler back to
   * verifyAccount once real Stitch credentials are in place — no other
   * changes needed, same payload shape, same DB columns.
   *
   * payload shape: identical to verifyAccount's.
   * Returns { status: 'unverified' }.
   */
  async saveAccount(payload) {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/api/open-banking/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message);
      return result;
    } catch (error) {
      console.error("Error saving bank account:", error);
      throw error;
    }
  },

  /**
   * Current payout account for the logged-in company, if any.
   * status: 'disconnected' | 'unverified' | 'verified'
   */
  async getStatus() {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/api/open-banking/status`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message);
      return result;
    } catch (error) {
      console.error("Error getting Open Banking status:", error);
      throw error;
    }
  },

  /**
   * Clears the payout account, e.g. before letting the partner submit
   * a different one.
   */
  async removeAccount() {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/api/open-banking/remove`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message);
      return result;
    } catch (error) {
      console.error("Error removing bank account:", error);
      throw error;
    }
  },

  /**
 * Full (unmasked) account number, fetched on demand when the owner
 * clicks "Show" in Settings. Not cached in getStatus() — only fetched
 * when explicitly requested, so it doesn't sit in state longer than needed.
 */
async revealAccountNumber() {
  try {
    const token = localStorage.getItem("authToken");
    const response = await fetch(`${API_URL}/api/open-banking/reveal`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    const result = await response.json();
    if (!response.ok) throw new Error(result.message);
    return result.accountNumber;
  } catch (error) {
    console.error("Error revealing account number:", error);
    throw error;
  }
},
};